import { useState, useEffect } from 'react';
import { Users, ShieldAlert, Plus, Edit, Trash2, Search, Calendar, Landmark, Info, History, Filter, RotateCcw, ArrowRight, CheckCircle2, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatPrice = (p) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(p || 0);

export default function AdminUsersAndLogs() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [orderLogs, setOrderLogs] = useState([]);
  const [stores, setStores] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const [loadingOrderLogs, setLoadingOrderLogs] = useState(false);

  // Search & Filters
  const [userSearch, setUserSearch] = useState('');
  const [logSearch, setLogSearch] = useState('');
  const [activeTab, setActiveTab] = useState('users');

  // Order Logs Filters
  const [orderLogSearch, setOrderLogSearch] = useState('');
  const [orderLogStartDate, setOrderLogStartDate] = useState('');
  const [orderLogEndDate, setOrderLogEndDate] = useState('');
  const [orderLogAction, setOrderLogAction] = useState('all');
  const [orderLogStatus, setOrderLogStatus] = useState('all');

  // Dialog State
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '', role: 'admin', store_id: 'all' });

  const getHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const { data } = await axios.get(`${API}/admin/users`, { withCredentials: true, headers: getHeaders() });
      setUsers(data);
    } catch {
      toast.error('Error al obtener usuarios');
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchLogs = async () => {
    setLoadingLogs(true);
    try {
      const { data } = await axios.get(`${API}/admin/logs`, { withCredentials: true, headers: getHeaders() });
      setLogs(data);
    } catch {
      toast.error('Error al obtener historial de acciones');
    } finally {
      setLoadingLogs(false);
    }
  };

  const fetchOrderLogs = async (customParams = {}) => {
    setLoadingOrderLogs(true);
    try {
      const params = {
        search: customParams.search !== undefined ? customParams.search : orderLogSearch,
        start_date: customParams.start_date !== undefined ? customParams.start_date : orderLogStartDate,
        end_date: customParams.end_date !== undefined ? customParams.end_date : orderLogEndDate,
        action: customParams.action !== undefined ? customParams.action : orderLogAction,
        status: customParams.status !== undefined ? customParams.status : orderLogStatus,
        limit: 300
      };
      // Clean empty keys
      Object.keys(params).forEach(k => {
        if (!params[k] || params[k] === 'all') delete params[k];
      });

      const { data } = await axios.get(`${API}/admin/order-logs`, {
        withCredentials: true,
        headers: getHeaders(),
        params
      });
      setOrderLogs(data);
    } catch {
      toast.error('Error al obtener movimientos de pedidos');
    } finally {
      setLoadingOrderLogs(false);
    }
  };

  const fetchStores = async () => {
    try {
      let storesData = [];
      try {
        const adminRes = await axios.get(`${API}/admin/stores`, { headers: getHeaders(), withCredentials: true });
        storesData = adminRes.data;
      } catch {
        const publicRes = await axios.get(`${API}/stores?include_hidden=true`);
        storesData = publicRes.data;
      }
      setStores(storesData);
    } catch {
      console.error('Error al obtener sedes');
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchStores();
  }, []);

  useEffect(() => {
    if (activeTab === 'logs') {
      fetchLogs();
    } else if (activeTab === 'order_logs') {
      fetchOrderLogs();
    }
  }, [activeTab]);

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setForm({
        name: user.name || '',
        email: user.email || '',
        password: '',
        phone: user.phone || '',
        role: user.role || 'admin',
        store_id: user.store_id || 'all'
      });
    } else {
      setEditingUser(null);
      setForm({ name: '', email: '', password: '', phone: '', role: 'admin', store_id: 'all' });
    }
    setIsDialogOpen(true);
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || (!editingUser && !form.password)) {
      toast.error('Completa los campos obligatorios');
      return;
    }

    const matchedStore = stores.find(s => s.id === form.store_id);
    const store_name = form.store_id === 'all' ? null : (matchedStore ? matchedStore.name : null);
    const store_id = form.store_id === 'all' ? null : form.store_id;

    const payload = {
      name: form.name,
      email: form.email,
      phone: form.phone || null,
      role: form.role,
      store_id,
      store_name
    };
    if (form.password) {
      payload.password = form.password;
    }

    try {
      if (editingUser) {
        await axios.put(`${API}/admin/users/${editingUser.id}`, payload, { withCredentials: true, headers: getHeaders() });
        toast.success('Usuario actualizado');
      } else {
        await axios.post(`${API}/admin/users`, payload, { withCredentials: true, headers: getHeaders() });
        toast.success('Usuario creado');
      }
      setIsDialogOpen(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar usuario');
    }
  };

  const handleDeleteUser = async (userToDelete) => {
    if (userToDelete.id === currentUser?.id || userToDelete.email === currentUser?.email) {
      toast.error('No puedes eliminar tu propio usuario actual');
      return;
    }
    if (!window.confirm(`¿Estás seguro de eliminar al usuario ${userToDelete.name}?`)) return;

    try {
      await axios.delete(`${API}/admin/users/${userToDelete.id}`, { withCredentials: true, headers: getHeaders() });
      toast.success('Usuario eliminado');
      fetchUsers();
    } catch {
      toast.error('Error al eliminar usuario');
    }
  };

  const filteredUsers = users.filter(u =>
    u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email?.toLowerCase().includes(userSearch.toLowerCase())
  );

  const filteredLogs = logs.filter(l =>
    l.user_email?.toLowerCase().includes(logSearch.toLowerCase()) ||
    l.user_name?.toLowerCase().includes(logSearch.toLowerCase()) ||
    l.action?.toLowerCase().includes(logSearch.toLowerCase())
  );

  const getActionLabel = (act) => {
    const labels = {
      login: 'Inicio de Sesión',
      create_user: 'Usuario Creado',
      update_user: 'Usuario Modificado',
      delete_user: 'Usuario Eliminado',
      update_order_status: 'Estado de Pedido Actualizado',
      create_product: 'Producto Creado',
      update_product: 'Producto Modificado',
      delete_product: 'Producto Eliminado',
      create_store: 'Sede Creada',
      update_store: 'Sede Modificada',
      delete_store: 'Sede Eliminada',
      update_settings: 'Configuración Guardada',
      create_promotion: 'Promoción Creada',
      update_promotion: 'Promoción Modificada',
      delete_promotion: 'Promoción Eliminada',
      create_transaction: 'Transacción Creada',
      delete_transaction: 'Transacción Eliminada'
    };
    return labels[act] || act;
  };

  const getActionColor = (act) => {
    if (act.includes('delete') || act.includes('remove')) return 'bg-red-100 text-red-800';
    if (act.includes('create') || act.includes('add')) return 'bg-green-100 text-green-800';
    if (act.includes('update') || act.includes('edit')) return 'bg-blue-100 text-blue-800';
    if (act === 'login') return 'bg-yellow-100 text-yellow-800';
    return 'bg-gray-100 text-gray-800';
  };

  const getOrderActionBadge = (action, details = {}) => {
    switch (action) {
      case 'create_order':
        return <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300 font-body text-xs font-semibold">✨ Pedido Creado</Badge>;
      case 'update_order_status': {
        const s = details.new_status;
        if (s === 'delivered') return <Badge className="bg-green-100 text-green-800 border border-green-300 font-body text-xs font-semibold">🎉 Entregado</Badge>;
        if (s === 'production') return <Badge className="bg-purple-100 text-purple-800 border border-purple-300 font-body text-xs font-semibold">👨‍🍳 A Producción</Badge>;
        if (s === 'approved') return <Badge className="bg-blue-100 text-blue-800 border border-blue-300 font-body text-xs font-semibold">👍 Aprobado</Badge>;
        if (s === 'delivery' || s === 'on_the_way') return <Badge className="bg-orange-100 text-orange-800 border border-orange-300 font-body text-xs font-semibold">🛵 En Entrega</Badge>;
        if (s === 'cancelled') return <Badge className="bg-red-100 text-red-800 border border-red-300 font-body text-xs font-semibold">❌ Cancelado</Badge>;
        return <Badge className="bg-yellow-100 text-yellow-800 border border-yellow-300 font-body text-xs font-semibold">Pendiente</Badge>;
      }
      case 'update_order':
        return <Badge className="bg-blue-100 text-blue-800 border border-blue-300 font-body text-xs">✏️ Modificado</Badge>;
      case 'register_payment':
        return <Badge className="bg-teal-100 text-teal-800 border border-teal-300 font-body text-xs font-semibold">💵 Pago Registrado</Badge>;
      case 'delete_payment':
        return <Badge className="bg-rose-100 text-rose-800 border border-rose-300 font-body text-xs">Pago Eliminado</Badge>;
      case 'add_order_novelty':
        return <Badge className="bg-amber-100 text-amber-800 border border-amber-300 font-body text-xs font-semibold">📌 Novedad</Badge>;
      case 'delete_order':
        return <Badge className="bg-red-100 text-red-800 border border-red-300 font-body text-xs font-semibold">🗑️ Eliminado</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800 border-none font-body text-xs">{action}</Badge>;
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-users-logs-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#46371c]">Usuarios y Logs</h1>
          <p className="font-body text-sm text-[#75664d] mt-1">
            Administra los usuarios del sistema y audita el historial de acciones por sede.
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="bg-[#46371c] hover:bg-[#2d2312] text-white rounded-xl font-body" data-testid="add-user-btn">
          <Plus className="w-4 h-4 mr-2" /> Nuevo Usuario
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-[#f4f1ea] border border-[#e5dfd3] p-1 rounded-xl mb-4">
          <TabsTrigger value="users" className="rounded-lg px-4 py-2 font-body text-sm flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:text-[#46371c] data-[state=active]:shadow-sm">
            <Users className="w-4 h-4" /> Usuarios
          </TabsTrigger>
          <TabsTrigger value="logs" className="rounded-lg px-4 py-2 font-body text-sm flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:text-[#46371c] data-[state=active]:shadow-sm">
            <ShieldAlert className="w-4 h-4" /> Historial del Sistema
          </TabsTrigger>
          <TabsTrigger value="order_logs" className="rounded-lg px-4 py-2 font-body text-sm flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:text-[#46371c] data-[state=active]:shadow-sm">
            <History className="w-4 h-4" /> Movimientos de Pedidos
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <Card className="p-6 border border-[#e5dfd3] bg-[#fcfbf9] rounded-2xl">
            <div className="relative mb-6 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#75664d]" />
              <input
                value={userSearch}
                onChange={e => setUserSearch(e.target.value)}
                placeholder="Buscar usuario por nombre o email..."
                className="w-full h-10 pl-9 pr-4 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
              />
            </div>

            {loadingUsers ? (
              <div className="flex justify-center py-12">
                <div className="w-6 h-6 border-2 border-[#a0d5ba] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredUsers.length === 0 ? (
              <p className="text-center py-12 font-body text-sm text-[#75664d]">No se encontraron usuarios</p>
            ) : (
              <div className="overflow-x-auto">
                <Table className="min-w-[650px]">
                  <TableHeader>
                    <TableRow className="bg-[#f4f1ea]">
                      <TableHead className="font-body text-xs font-semibold text-[#46371c]">Nombre</TableHead>
                      <TableHead className="font-body text-xs font-semibold text-[#46371c]">Email</TableHead>
                      <TableHead className="font-body text-xs font-semibold text-[#46371c]">Rol</TableHead>
                      <TableHead className="font-body text-xs font-semibold text-[#46371c]">Sede Asignada</TableHead>
                      <TableHead className="font-body text-xs font-semibold text-[#46371c] text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                  {filteredUsers.map(u => (
                    <TableRow key={u.id}>
                      <TableCell className="font-body text-sm font-medium text-[#46371c]">{u.name}</TableCell>
                      <TableCell className="font-body text-sm text-[#75664d]">{u.email}</TableCell>
                      <TableCell>
                        <Badge className={`text-xs ${
                          u.role === 'admin' 
                            ? 'bg-purple-100 text-purple-800' 
                            : u.role === 'domiciliario'
                            ? 'bg-orange-100 text-orange-800'
                            : (u.role === 'produccion' || u.role === 'producción')
                            ? 'bg-blue-100 text-blue-800'
                            : (u.role === 'vendedora' || u.role === 'vendedor')
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {u.role === 'admin' ? 'Administrador' : u.role === 'domiciliario' ? 'Domiciliario' : (u.role === 'produccion' || u.role === 'producción') ? 'Producción' : (u.role === 'vendedora' || u.role === 'vendedor') ? 'Vendedor/a' : 'Cliente'}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-body text-sm text-[#46371c]">
                        {u.store_name ? (
                          <span className="flex items-center gap-1.5"><Landmark className="w-3.5 h-3.5 text-[#a0d5ba]" />{u.store_name}</span>
                        ) : (
                          <span className="text-[#75664d] italic">Todas las sedes / Global</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right space-x-2">
                        <Button onClick={() => handleOpenDialog(u)} size="sm" variant="ghost" className="h-8 w-8 p-0 rounded-lg text-[#75664d] hover:text-[#46371c]">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button onClick={() => handleDeleteUser(u)} size="sm" variant="ghost" className="h-8 w-8 p-0 rounded-lg text-red-500 hover:text-red-700 hover:bg-red-50">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="logs">
          <Card className="p-6 border border-[#e5dfd3] bg-[#fcfbf9] rounded-2xl">
            <div className="relative mb-6 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#75664d]" />
              <input
                value={logSearch}
                onChange={e => setLogSearch(e.target.value)}
                placeholder="Buscar logs por email, acción o usuario..."
                className="w-full h-10 pl-9 pr-4 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
              />
            </div>

            {loadingLogs ? (
              <div className="flex justify-center py-12">
                <div className="w-6 h-6 border-2 border-[#a0d5ba] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredLogs.length === 0 ? (
              <p className="text-center py-12 font-body text-sm text-[#75664d]">No hay historial de acciones registrado</p>
            ) : (
              <div className="overflow-x-auto">
                <Table className="min-w-[650px]">
                  <TableHeader>
                    <TableRow className="bg-[#f4f1ea]">
                      <TableHead className="font-body text-xs font-semibold text-[#46371c]">Fecha</TableHead>
                      <TableHead className="font-body text-xs font-semibold text-[#46371c]">Usuario</TableHead>
                      <TableHead className="font-body text-xs font-semibold text-[#46371c]">Sede Contexto</TableHead>
                      <TableHead className="font-body text-xs font-semibold text-[#46371c]">Acción</TableHead>
                      <TableHead className="font-body text-xs font-semibold text-[#46371c]">Detalles</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredLogs.map(log => (
                      <TableRow key={log.id}>
                        <TableCell className="font-body text-xs text-[#75664d] whitespace-nowrap">
                          <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{new Date(log.created_at).toLocaleString('es-CO')}</span>
                        </TableCell>
                        <TableCell>
                          <p className="font-body text-sm font-medium text-[#46371c]">{log.user_name}</p>
                          <p className="font-body text-[10px] text-[#75664d]">{log.user_email}</p>
                        </TableCell>
                        <TableCell className="font-body text-sm text-[#46371c]">
                          {log.store_name ? log.store_name : <span className="text-[#75664d] text-xs italic">Global / Todas</span>}
                        </TableCell>
                        <TableCell>
                          <Badge className={`text-xs font-normal ${getActionColor(log.action)}`}>
                            {getActionLabel(log.action)}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-[200px]">
                          <span className="font-body text-xs text-[#75664d] truncate flex items-center gap-1" title={JSON.stringify(log.details)}>
                            <Info className="w-3.5 h-3.5 flex-shrink-0 text-[#a0d5ba]" />
                            {Object.entries(log.details || {}).map(([key, val]) => `${key}: ${val}`).join(', ')}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Auditoría y Movimientos de Pedidos */}
        <TabsContent value="order_logs">
          <Card className="p-6 border border-[#e5dfd3] bg-[#fcfbf9] rounded-2xl space-y-4">
            <div>
              <h3 className="font-heading text-lg font-bold text-[#46371c]">Historial de Movimientos de Pedidos</h3>
              <p className="font-body text-xs text-[#75664d] mt-0.5">
                Audita quién subió el pedido, quién lo envió a producción, quién lo entregó, novedades y pagos registrados.
              </p>
            </div>

            {/* Filtros de Búsqueda */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                fetchOrderLogs();
              }}
              className="p-4 bg-[#f4f1ea] border border-[#e5dfd3] rounded-xl grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end"
            >
              <div className="lg:col-span-2">
                <label className="font-body text-xs text-[#75664d] mb-1 block font-medium">Búsqueda rápida</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#75664d]" />
                  <input
                    value={orderLogSearch}
                    onChange={e => setOrderLogSearch(e.target.value)}
                    placeholder="Buscar por código (#), cliente o usuario..."
                    className="w-full h-10 pl-9 pr-3 rounded-xl border border-[#e5dfd3] bg-white font-body text-xs sm:text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
                  />
                </div>
              </div>

              <div>
                <label className="font-body text-xs text-[#75664d] mb-1 block font-medium">Desde</label>
                <input
                  type="date"
                  value={orderLogStartDate}
                  onChange={e => setOrderLogStartDate(e.target.value)}
                  className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] bg-white font-body text-xs text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
                />
              </div>

              <div>
                <label className="font-body text-xs text-[#75664d] mb-1 block font-medium">Hasta</label>
                <input
                  type="date"
                  value={orderLogEndDate}
                  onChange={e => setOrderLogEndDate(e.target.value)}
                  className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] bg-white font-body text-xs text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
                />
              </div>

              <div>
                <label className="font-body text-xs text-[#75664d] mb-1 block font-medium">Acción</label>
                <Select value={orderLogAction} onValueChange={setOrderLogAction}>
                  <SelectTrigger className="w-full h-10 rounded-xl border-[#e5dfd3] bg-white text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las acciones</SelectItem>
                    <SelectItem value="create_order">Creación de Pedido</SelectItem>
                    <SelectItem value="update_order_status">Cambio de Estado</SelectItem>
                    <SelectItem value="register_payment">Abono / Pago</SelectItem>
                    <SelectItem value="add_order_novelty">Novedad</SelectItem>
                    <SelectItem value="update_order">Edición de Pedido</SelectItem>
                    <SelectItem value="delete_order">Eliminación de Pedido</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2">
                <Button type="submit" className="bg-[#46371c] hover:bg-[#2d2312] text-white rounded-xl h-10 flex-1 text-xs font-medium">
                  <Filter className="w-3.5 h-3.5 mr-1.5" /> Filtrar
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setOrderLogSearch('');
                    setOrderLogStartDate('');
                    setOrderLogEndDate('');
                    setOrderLogAction('all');
                    setOrderLogStatus('all');
                    fetchOrderLogs({ search: '', start_date: '', end_date: '', action: 'all', status: 'all' });
                  }}
                  className="rounded-xl border-[#e5dfd3] bg-white h-10 px-3 text-[#75664d] hover:text-[#46371c]"
                  title="Limpiar filtros"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                </Button>
              </div>
            </form>

            {loadingOrderLogs ? (
              <div className="flex justify-center py-16">
                <div className="w-7 h-7 border-2 border-[#a0d5ba] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : orderLogs.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl border border-dashed border-[#e5dfd3]">
                <History className="w-10 h-10 text-[#e5dfd3] mx-auto mb-2" />
                <p className="font-body text-sm font-semibold text-[#46371c]">No se encontraron movimientos registrados</p>
                <p className="font-body text-xs text-[#75664d] mt-1">Prueba cambiando los filtros de búsqueda o fechas</p>
              </div>
            ) : (
              <div className="overflow-x-auto bg-white rounded-xl border border-[#e5dfd3]">
                <Table className="min-w-[850px]">
                  <TableHeader>
                    <TableRow className="bg-[#f4f1ea]">
                      <TableHead className="font-body text-xs font-semibold text-[#46371c]">Fecha y Hora</TableHead>
                      <TableHead className="font-body text-xs font-semibold text-[#46371c]">Pedido</TableHead>
                      <TableHead className="font-body text-xs font-semibold text-[#46371c]">Movimiento / Acción</TableHead>
                      <TableHead className="font-body text-xs font-semibold text-[#46371c]">Usuario Responsable</TableHead>
                      <TableHead className="font-body text-xs font-semibold text-[#46371c]">Sede</TableHead>
                      <TableHead className="font-body text-xs font-semibold text-[#46371c]">Detalle del Movimiento</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orderLogs.map(log => {
                      const details = log.details || {};
                      const tracking = details.tracking_code || details.order_id?.slice(-6) || '---';
                      const customer = details.customer_name;

                      return (
                        <TableRow key={log.id} className="hover:bg-[#fcfbf9]">
                          <TableCell className="font-body text-xs text-[#75664d] whitespace-nowrap">
                            <span className="flex items-center gap-1.5 font-medium text-[#46371c]">
                              <Clock className="w-3.5 h-3.5 text-[#a0d5ba]" />
                              {new Date(log.created_at).toLocaleString('es-CO')}
                            </span>
                          </TableCell>

                          <TableCell>
                            <span className="font-body text-sm font-bold text-[#46371c]">#{tracking}</span>
                            {customer && (
                              <p className="font-body text-xs text-[#75664d] truncate max-w-[140px]">{customer}</p>
                            )}
                          </TableCell>

                          <TableCell>
                            {getOrderActionBadge(log.action, details)}
                          </TableCell>

                          <TableCell>
                            <p className="font-body text-xs font-semibold text-[#46371c]">{log.user_name || 'Sistema'}</p>
                            <p className="font-body text-[10px] text-[#75664d]">{log.user_email || ''}</p>
                            {log.user_role && (
                              <span className="inline-block mt-0.5 text-[9px] font-medium px-1.5 py-0.2 bg-gray-100 text-gray-700 rounded-md capitalize">
                                {log.user_role}
                              </span>
                            )}
                          </TableCell>

                          <TableCell className="font-body text-xs text-[#46371c]">
                            {log.store_name || details.store_name ? (
                              <span className="flex items-center gap-1 text-[#46371c]">
                                <Landmark className="w-3 h-3 text-[#a0d5ba]" />
                                {log.store_name || details.store_name}
                              </span>
                            ) : (
                              <span className="text-[#75664d] italic text-[11px]">Sede Central / Global</span>
                            )}
                          </TableCell>

                          <TableCell className="max-w-[280px]">
                            {log.action === 'update_order_status' ? (
                              <div className="text-xs font-body text-[#46371c] flex items-center gap-1.5">
                                <span className="text-[#75664d] capitalize">{details.previous_status || 'Inicio'}</span>
                                <ArrowRight className="w-3 h-3 text-[#a0d5ba] shrink-0" />
                                <span className="font-semibold capitalize text-[#46371c]">{details.new_status}</span>
                              </div>
                            ) : log.action === 'register_payment' ? (
                              <p className="text-xs font-body text-emerald-800 font-medium">
                                Abono de {formatPrice(details.amount)} ({details.method})
                              </p>
                            ) : log.action === 'delete_payment' ? (
                              <p className="text-xs font-body text-rose-800 font-medium">
                                Pago eliminado por {formatPrice(details.amount)}
                              </p>
                            ) : log.action === 'add_order_novelty' ? (
                              <p className="text-xs font-body text-amber-900 bg-amber-50 p-1.5 rounded-lg border border-amber-200">
                                "{details.text}"
                              </p>
                            ) : log.action === 'create_order' ? (
                              <p className="text-xs font-body text-[#46371c]">
                                Pedido creado {details.total ? `por ${formatPrice(details.total)}` : ''}
                              </p>
                            ) : (
                              <span className="font-body text-xs text-[#75664d] truncate block" title={JSON.stringify(details)}>
                                {Object.entries(details).map(([k, v]) => `${k}: ${v}`).join(', ')}
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* User Form Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md bg-white border border-[#e5dfd3] rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold text-[#46371c]">
              {editingUser ? 'Editar Usuario' : 'Nuevo Usuario Administrador'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSaveUser} className="space-y-4 py-4">
            <div>
              <label className="font-body text-xs text-[#75664d] mb-1 block">Nombre completo *</label>
              <input
                value={form.name}
                onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                required
                className="w-full h-10 px-4 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
              />
            </div>
            <div>
              <label className="font-body text-xs text-[#75664d] mb-1 block">Email *</label>
              <input
                type="email"
                value={form.email}
                onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))}
                required
                className="w-full h-10 px-4 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
              />
            </div>
            <div>
              <label className="font-body text-xs text-[#75664d] mb-1 block">Contraseña {editingUser && '(dejar vacío para no cambiar)'} *</label>
              <input
                type="password"
                value={form.password}
                onChange={e => setForm(prev => ({ ...prev, password: e.target.value }))}
                required={!editingUser}
                className="w-full h-10 px-4 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
              />
            </div>
            <div>
              <label className="font-body text-xs text-[#75664d] mb-1 block">Teléfono</label>
              <input
                value={form.phone}
                onChange={e => setForm(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full h-10 px-4 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
              />
            </div>
            <div>
              <label className="font-body text-xs text-[#75664d] mb-1 block">Rol *</label>
              <Select value={form.role} onValueChange={val => setForm(prev => ({ ...prev, role: val }))}>
                <SelectTrigger className="w-full rounded-xl border-[#e5dfd3]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrador</SelectItem>
                  <SelectItem value="domiciliario">Domiciliario</SelectItem>
                  <SelectItem value="produccion">Producción</SelectItem>
                  <SelectItem value="vendedora">Vendedora</SelectItem>
                  <SelectItem value="customer">Cliente</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="font-body text-xs text-[#75664d] mb-1 block">Sede Asignada *</label>
              <Select value={form.store_id} onValueChange={val => setForm(prev => ({ ...prev, store_id: val }))}>
                <SelectTrigger className="w-full rounded-xl border-[#e5dfd3]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las Sedes (Super Admin / Global)</SelectItem>
                  {stores.map(store => (
                    <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <DialogFooter className="pt-4 border-t border-[#e5dfd3]/60 flex gap-2">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-xl border-[#46371c] text-[#46371c]">
                Cancelar
              </Button>
              <Button type="submit" className="bg-[#46371c] hover:bg-[#2d2312] text-white rounded-xl">
                Guardar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
